import {collection, doc, Firestore, getDoc, getDocs, query, setDoc, where} from "firebase/firestore";
import {Session} from "../auth/session";
import React, {useEffect, useState} from "react";
import Navbar from "../components/nav/Navbar";
import Spinner, {SpinnerSize} from "../components/spinner/Spinner";
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    FormControlLabel,
    FormGroup,
    TextField
} from "@mui/material";
import {Navigate} from "react-router";
import {Wayfare} from "../defs/wayfare";
import {CountryGroup} from "../defs/group";
import {countries} from "../countries";
import {Footer} from "../components/Footer";

interface Props {
    session: Session | null
    store: Firestore
}

export function Edit({session, store}: Props) {
    const [username, setUsername] = useState(session && session.username ? session.username : "");
    const wayfares = collection(store, "wayfares");
    const [profile, setProfile] = useState<Wayfare | null>(null);

    const [desiredUsername, setDesiredUsername] = useState("");
    const [usernameTaken, setUsernameTaken] = useState(false);

    useEffect(() => {
        if (!session) return;
        if (!username) return;

        const q = query(wayfares, where("uid", "==", session.uid));
        getDocs(q).then(docs => {
            if (docs.size === 0) {
                return;
            }

            docs.forEach(doc => {
                const username = doc.id;
                const profile: Wayfare = doc.data() as unknown as Wayfare;
                setProfile(profile);
            })
        });
    }, [username]);

    if (!session)
        return <Navigate to="/auth"/>;

    const createProfile = async () => {
        const existing = await getDoc(doc(store, "wayfares", desiredUsername));
        if (existing.exists()) {
            setUsernameTaken(true);
            return;
        }

        const wayfare: Wayfare = {
            key: desiredUsername,
            uid: session.uid,
            name: session.name,
            visited: []
        }
        await setDoc(doc(store, "wayfares", desiredUsername), wayfare);
        localStorage.setItem("username", desiredUsername);
        setUsername(desiredUsername);
    }

    const toggleCountry = async (countryCode: string, profile: Wayfare) => {
        const wayfare: Wayfare = Object.assign({}, profile);
        if (profile.visited.includes(countryCode)) {
            wayfare.visited = wayfare.visited.filter(code => code !== countryCode);
        } else {
            wayfare.visited.push(countryCode);
        }
        await setDoc(doc(store, "wayfares", profile.key), wayfare);
        setProfile(wayfare);
    }

    const countryList = function (group: CountryGroup, profile: Wayfare) {
        return <div className="country-group">
            <h2>{CountryGroup.displayName(group)}</h2>
            {countries
                .filter(country => country.group === group)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(country => <FormGroup key={country.countryCode}>
                        <FormControlLabel
                            control={
                                <Checkbox checked={profile.visited.includes(country.countryCode)}
                                          onClick={() => toggleCountry(country.countryCode, profile)}/>
                            }
                            label={<span className="flag-polyfill">
                                {country.flag} {country.name}
                            </span>}/>
                    </FormGroup>
                )}
        </div>
    }

    return <div id="app-root">
        <Navbar/>
        <div className="document">
            {!profile
                ? <div className="text-center py-5 my-5">
                    <Spinner size={SpinnerSize.SIZE_128}/>
                </div>
                : <>
                    <h1>Edit Wayfare</h1>
                    <p>
                        Modify your visited countries and territories by clicking the check boxes below.
                        Changes are automatically saved.
                    </p>
                    <hr/>
                    <div className="text-center py-3 fw-bold">
                        View your public profile at&nbsp;
                        <a href={"/@" + profile.key} target="_blank" rel="noopener noreferrer">
                            wayf.re/@{profile.key} &raquo;
                        </a>
                    </div>
                    <hr/>
                    <div className="row">
                        <div className="col-md-4">
                            {countryList(CountryGroup.NORTH_AMERICA, profile)}
                            {countryList(CountryGroup.EUROPE, profile)}
                        </div>
                        <div className="col-md-4">
                            {countryList(CountryGroup.CENTRAL_AMERICA, profile)}
                            {countryList(CountryGroup.AFRICA, profile)}
                        </div>
                        <div className="col-md-4">
                            {countryList(CountryGroup.SOUTH_AMERICA, profile)}
                            {countryList(CountryGroup.ASIA, profile)}
                            {countryList(CountryGroup.OCEANIA, profile)}
                        </div>
                    </div>
                </>}
        </div>
        <Footer/>
        <Dialog open={!profile && !username}
                onSubmit={createProfile}>
            <DialogContent>
                <DialogContentText>
                    Select a username for your Wayfare profile, which will be
                    publicly accessible at <strong>wayf.re/@username</strong>
                </DialogContentText>
                <TextField
                    autoFocus
                    required
                    error={usernameTaken}
                    value={desiredUsername}
                    onChange={e => setDesiredUsername(e.target.value.toLowerCase().replaceAll(/[^A-Za-z0-9]+/g, ""))}
                    margin="dense"
                    id="username"
                    name="username"
                    label="Username"
                    type="text"
                    fullWidth
                    variant="standard"
                />
            </DialogContent>
            <DialogActions>
                <Button type="submit" onClick={createProfile}>Choose</Button>
            </DialogActions>
        </Dialog>
    </div>
}
